"use client";

import * as React from "react";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Link } from "react-router-dom";

// ── Types ────────────────────────────────────────────────────────────────────
export interface AppMenuItem {
  label: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  href?: string;
  destructive?: boolean;
  disabled?: boolean;
}

export interface AppMenuProps {
  items: AppMenuItem[];
  trigger: React.ReactNode;
  align?: "start" | "center" | "end";
  itemSize?: "default" | "xl";
}

// ── Component ────────────────────────────────────────────────────────────────
export function OptionMenu({ items, trigger, align = "end", itemSize = "default" }: AppMenuProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        sideOffset={6}
        className={cn(
          "min-w-[140px] rounded-xl border border-border-light bg-background shadow-md p-2"
        )}
      >
        {items.map((item, i) => (
          <div key={i}>
            <DropdownMenuItem
              disabled={item.disabled}
              onClick={() => item.onClick?.()}
              asChild={!!item.href}
              className={cn(
                "flex items-center gap-2 rounded-lg px-3 font-medium transition-colors cursor-pointer py-2.5",
                "hover:bg-muted disabled:pointer-events-none disabled:opacity-50",
                item.destructive ? "text-destructive hover:bg-destructive/10 focus:bg-destructive/10 focus:text-destructive" : "text-foreground"
              )}
            >
              {item.href ? (
                <Link to={item.href}>
                  {item.icon && <span className="shrink-0">{item.icon}</span>}
                  {item.label}
                </Link>
              ) : (
                <>
                  {item.icon && <span className="shrink-0">{item.icon}</span>}
                  {item.label}
                </>
              )}
            </DropdownMenuItem>
            {i < items.length - 1 && (
              <div className="h-px bg-border-light my-1" />
            )}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
